import * as React from "react";

import { useMergedRefs } from "@fluentui/react-utilities";

import {
  AppFlyoutAnchorProps,
  AppFlyoutAnchorState,
} from "./AppFlyoutAnchor.types";
import { InputMethod as CommonInputMethod } from "@1js/acui-common";
import {
  AppSplitButtonProps,
  AppSplitButtonState,
} from "../../AppSplitButton/next";

// Input methods for invoking the menu.
// This is used to set the focus correctly when the menu is opened.
export type InputMethod = "keyboard" | "mouse";

/**
 * Takes a menu button state and makes sure that the control receives a 'checked' prop,
 * thus allowing it to be styled when its menu is expanded. It further makes sure that
 * no tooltips on the original button are shown while the menu is expanded.
 */
export const useControlledContextualMenuByMenuButton = (
  props: AppFlyoutAnchorProps | AppSplitButtonProps,
  state: AppFlyoutAnchorState | AppSplitButtonState,
  targetRef?: React.Ref<HTMLElement>
) => {
  const { onAfterMenuDismiss, id, location, onExecute } = props;

  // This tracks if the contextual menu should be shown and the input method
  // that caused it to be shown.
  const [showContextualMenu, setShowContextualMenu] = React.useState<
    false | InputMethod
  >(false);
  const firstRender = React.useRef(true);

  const onHideContextualMenu = React.useCallback(
    () => setShowContextualMenu(false),
    []
  );

  const onButtonClick = React.useCallback(
    (
      ev: React.MouseEvent<HTMLButtonElement> &
        React.MouseEvent<HTMLAnchorElement>
    ) => {
      ev.preventDefault(); // don't navigate
      if (!showContextualMenu) {
        onExecute &&
          onExecute({
            id,
            location,
            inputMethod: CommonInputMethod.Mouse,
          });
        setShowContextualMenu("mouse");
      } else {
        setShowContextualMenu(false);
      }
    },
    [showContextualMenu, onExecute, id, location]
  );

  const onButtonKeyDown = React.useCallback(
    (ev: React.KeyboardEvent<HTMLElement>) => {
      if (
        (ev.altKey && ev.key === "ArrowDown") ||
        ev.key === "Enter" ||
        ev.key === " "
      ) {
        ev.preventDefault();
        onExecute &&
          onExecute({
            id,
            location,
            inputMethod: CommonInputMethod.Keyboard,
          });
        setShowContextualMenu("keyboard");
      }
    },
    [onExecute, id, location]
  );

  React.useEffect(() => {
    if (firstRender.current) {
      // Prevent this effect from running on first render
      firstRender.current = false;
    } else if (!showContextualMenu && onAfterMenuDismiss) {
      onAfterMenuDismiss(state.menuButton.id);
    }
  }, [showContextualMenu, onAfterMenuDismiss, state.menuButton.id]);

  state.menuButton.onClick = onButtonClick;
  state.menuButton.onKeyDown = onButtonKeyDown;
  state.menuButton["aria-expanded"] = !!showContextualMenu;

  state.menu.hidden = !showContextualMenu;
  state.menu.onDismiss = onHideContextualMenu;
  state.menu.onItemClick = onHideContextualMenu;
  state.menu.shouldFocusOnContainer = showContextualMenu === "mouse";

  // Hooks can't be used conditionally, making them outside of if-statement
  // and only using them in the else case
  const buttonRef = React.useRef(null);
  const mergedButtonRef = useMergedRefs(state.menuButton.ref, buttonRef);

  if (targetRef) {
    // If targetRef given, set as menu target
    state.menu.target = targetRef as React.RefObject<HTMLElement>;
  } else {
    // If not given, default to menuButton on state
    state.menuButton.ref = mergedButtonRef;
    state.menu.target = buttonRef;
  }
};
